<template>
  <span v-if="showIcon" class="IntrusionPartitionSmartIcon" v-tooltip="tooltip">
    <FaIcon :icon="view.icon" :class="colorClass" />
    <FaIcon
      v-if="isCountdown"
      class="IntrusionPartitionSmartIcon__countdown fa-pulse"
      :class="colorClass"
      :icon="COUNTDOWN_ICON"
    />
    <span v-if="showBadge" class="IntrusionPartitionSmartIcon__count">{{ view.count }}</span>
  </span>
  <span v-else-if="showLabel" :class="colorClass">{{ view.label }}</span>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { ICON } from '@/constants/icons';
import type { IntrusionPartitionStatusFlag } from '@/views/intrusion/types';
import {
  buildPartitionSmartIconSummary,
  getPartitionAggregateDescriptor,
  type IntrusionPartitionSmartIconSummary
} from '@/views/intrusion/utilities/intrusion-smart-icon-status';
import { getPartitionStatusFlags } from '@/views/intrusion/utilities/map-intrusion';
import { getStatusCellVariant } from '@/views/intrusion/utilities/status-variant';

defineOptions({ name: 'IntrusionPartitionSmartIcon' });

/** A raw partition row (as returned by a device/panel query): the icon decodes its own status flags. */
type PartitionStatusSource = Parameters<typeof getPartitionStatusFlags>[0];

const props = withDefaults(
  defineProps<{
    /** Single partition: its own status string + flags. */
    status?: string;
    statusFlags?: readonly IntrusionPartitionStatusFlag[];
    isSyncing?: boolean;
    /** Aggregate: a count summary of many partitions. Drives the label/colour instead when set. */
    summary?: IntrusionPartitionSmartIconSummary;
    /**
     * Aggregate (raw): the partition rows themselves, still carrying `stateValues`/`alarmValues`. The
     * icon decodes + summarises them, so callers with raw panel data don't pre-build a `summary`.
     */
    partitions?: readonly PartitionStatusSource[];
    /** Overlay the count on the glyph (aggregate contexts with no separate count beside it). */
    showCount?: boolean;
    type?: 'icon' | 'label';
  }>(),
  { type: 'icon' }
);

// A pre-built `summary` wins; otherwise raw `partitions` are decoded + summarised here.
const aggregateSummary = computed<IntrusionPartitionSmartIconSummary | undefined>(() => {
  if (props.summary) return props.summary;
  if (!props.partitions) return undefined;

  return buildPartitionSmartIconSummary(props.partitions.map((partition) => ({ statusFlags: getPartitionStatusFlags(partition) })));
});

const view = computed(() => {
  if (aggregateSummary.value) {
    return { ...getPartitionAggregateDescriptor(aggregateSummary.value), prefix: 'Partitions' };
  }

  return {
    variant: getStatusCellVariant(Boolean(props.isSyncing), props.statusFlags ?? []),
    icon: ['fas', ICON.LAYER_GROUP] as ['fas', string],
    label: props.isSyncing ? 'Syncing...' : props.status || 'Unknown',
    count: 0,
    prefix: 'Partition'
  };
});

const COUNTDOWN_ICON: ['fas', string] = ['fas', ICON.SPINNER];

const isCountdown = computed(() => view.value.variant === 'transitional');

const colorClass = computed(() => `IntrusionStatusColor--${view.value.variant}`);
const showIcon = computed(() => props.type === 'icon');
const showLabel = computed(() => props.type === 'label');
const showBadge = computed(() => Boolean(props.showCount) && view.value.count > 1);
const tooltip = computed(() => ({
  triggers: ['click', 'hover', 'focus'],
  content: `${view.value.prefix}: ${view.value.label}`
}));
</script>

<style scoped lang="scss">
.IntrusionPartitionSmartIcon {
  position: relative;
  display: inline-flex;

  &__countdown {
    position: absolute;
    top: calc(-1 * var(--size-1));
    left: calc(-1 * var(--size-2));
    font-size: 0.6rem;
    border-radius: 50%;
    background: var(--color-background-600);
  }

  &__count {
    position: absolute;
    top: calc(-1 * var(--size-1));
    right: calc(-1 * var(--size-2));
    min-width: var(--size-4);
    height: var(--size-4);
    padding: 0 var(--size-1);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--size-4);
    background: var(--color-background-600);
    color: var(--color-text);
    font-size: 0.65rem;
    font-weight: $font-bold;
    line-height: 1;
  }
}
</style>
